import { useReducer } from "react";

import { TABLE_MODE_OPTIONS } from "../constants";

/**
 * @typedef {Object} TableSorting
 * @property {string} id
 * @property {boolean} desc
 */

/**
 * @typedef {Object} TableFilter
 * @property {string} id
 * @property {string} value
 */

/**
 * @typedef {Object} DataTableState
 * @property {number} pageIndex
 * @property {number} pageSize
 * @property {Array<TableFilter>} columnsFilter
 * @property {Array<TableSorting>} columnsSorting
 * @property {Array<any>} data
 * @property {number} totalRows
 * @property {"client" | "server"} mode
 */

const initialDataTableState = {
  pageIndex: 0,
  pageSize: 10,
  columnsFilter: [],
  columnsSorting: [{ id: "creationDate", desc: true }],
  data: [],
  totalRows: 0,
  mode: TABLE_MODE_OPTIONS.SERVER, // server | client
};

const ACTIONS = {
  SET_MODE: "mode",
  SET_PAGINATION: "pagination",
  SET_PAGE_INDEX: "pageIndex",
  SET_FILTER: "filter",
  SET_SORTING: "sorting",
  SET_DATA: "data",
  FETCHING: "fetching",
  RESET: "reset",
};

const TABLE_ACTION_CREATORS = {
  setMode: (isClientSidePagination, statusColumnFilter) => ({
    type: ACTIONS.SET_MODE,
    payload: { isClientSidePagination, statusColumnFilter },
  }),
  setPagination: (pageIndex, pageSize) => ({
    type: ACTIONS.SET_PAGINATION,
    payload: { pageIndex, pageSize },
  }),
  setPageIndex: (pageIndex) => ({
    type: ACTIONS.SET_PAGE_INDEX,
    payload: { pageIndex },
  }),
  setColumnsFilter: (columnsFilter) => ({
    type: ACTIONS.SET_FILTER,
    payload: { columnsFilter },
  }),
  setColumnsSorting: (columnsSorting) => ({
    type: ACTIONS.SET_SORTING,
    payload: { columnsSorting },
  }),
  // setData: (data, totalRows) => ({
  //   type: ACTIONS.SET_DATA,
  //   payload: { data, totalRows },
  // }),
  // fetching: () => ({
  //   type: ACTIONS.FETCHING,
  // }),
  reset: () => ({
    type: ACTIONS.RESET,
  }),
};

const reducerDataTable = (state, action) => {
  switch (action.type) {
    case "mode": {
      const newMode = action.payload.isClientSidePagination
        ? TABLE_MODE_OPTIONS.CLIENT
        : TABLE_MODE_OPTIONS.SERVER;

      if (
        !action.payload.isClientSidePagination &&
        state.mode === TABLE_MODE_OPTIONS.SERVER
      ) {
        // no change
        // console.log("Table mode unchanged (server-side).");
        return state;
      }

      if (newMode === TABLE_MODE_OPTIONS.CLIENT) {
        const statusFilter = {
          id: "status",
          value: action.payload.statusColumnFilter || "",
        };

        const hasStatusFilter = state.columnsFilter.some(
          (filter) => filter.id === "status",
        );

        const columnsFilter = hasStatusFilter
          ? state.columnsFilter.map((filter) =>
              filter.id === "status" ? statusFilter : filter,
            )
          : [...state.columnsFilter, statusFilter];

        // console.log("Setting client-side mode");

        return {
          ...state,
          pageIndex: 0, // Reset to first page when mode changes
          mode: newMode,
          columnsFilter,
        };
      }

      // remove status column filter for server-side mode
      const stateWithoutStatusFilter = state.columnsFilter.filter(
        (filter) => filter.id !== "status",
      );

      // console.log("Setting server-side mode");
      return {
        ...state,
        pageIndex: 0, // Reset to first page when mode changes
        mode: newMode,
        columnsFilter: stateWithoutStatusFilter,
      };
    }

    case "pagination":
      return {
        ...state,
        pageIndex: action.payload.pageIndex,
        pageSize: action.payload.pageSize,
      };
    case "pageIndex":
      return {
        ...state,
        pageIndex: action.payload.pageIndex,
      };
    case "filter":
      return {
        ...state,
        pageIndex: 0, // Reset to first page when filter changes
        columnsFilter: action.payload.columnsFilter,
      };

    case "sorting":
      return {
        ...state,
        pageIndex: 0, // Reset to first page when sorting changes
        columnsSorting: action.payload.columnsSorting,
      };

    // case "data":
    //   return {
    //     ...state,
    //     data: action.payload.data,
    //     totalRows: action.payload.totalRows,
    //   };
    // case "fetching":
    //   return {
    //     ...state,
    //     data: [],
    //   };

    case "reset":
      return initialDataTableState;
    default:
      return state;
  }
};
/**
 *
 * @returns {[DataTableState, Function]}
 */

const useTableController = () => {
  const actions = useReducer(reducerDataTable, initialDataTableState);

  return actions;
};

export { TABLE_ACTION_CREATORS, useTableController };
